import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatEuros } from "@sundial/core";
import { useReportRange, useUrssaf } from "../api/queries.js";

type Period = "month" | "ytd" | "y12";

function todayParis(): string {
  return new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}
function monthName(ym: string, opts: Intl.DateTimeFormatOptions): string {
  const [y, m] = ym.split("-").map((n) => parseInt(n, 10));
  return new Intl.DateTimeFormat("fr-FR", opts).format(new Date(y!, (m ?? 1) - 1, 1));
}
function fmtDays(n: number): string {
  return n.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

function fmtPct(n: number): string {
  return n.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

/**
 * The Home Revenue card (SundialRevenueCard.dc.html): HT revenue with a period
 * toggle — Ce mois / Depuis janvier / 12 mois — computed from /reports/range,
 * so YTD and trailing-12 are REAL sums, not a repeated month figure. Carries the
 * "à mettre de côté" (URSSAF + TVA) estimate, which rescopes with the period AND
 * the client filter. The annual micro ceiling stays person-level (CeilingCard).
 */
export function RevenueCard({ month, clientId, clientName }: { month: string; clientId?: string; clientName?: string }) {
  const { t } = useTranslation();
  const { data: urssaf } = useUrssaf();
  const [period, setPeriod] = useState<Period>("month");

  const today = todayParis();
  const year = today.slice(0, 4);
  const [yy, mm] = month.split("-").map((n) => parseInt(n, 10));
  const monthEnd = `${month}-${String(new Date(yy!, mm!, 0).getDate()).padStart(2, "0")}`;
  // Trailing 12 months: start of the month 11 months before this one.
  const t12 = new Date(yy!, mm! - 1 - 11, 1);
  const y12From = `${t12.getFullYear()}-${String(t12.getMonth() + 1).padStart(2, "0")}-01`;

  const monthR = useReportRange(`${month}-01`, monthEnd, "both", clientId);
  const ytdR = useReportRange(`${year}-01-01`, today, "both", clientId);
  const y12R = useReportRange(y12From, today, "both", clientId);

  const data = period === "month" ? monthR.data : period === "ytd" ? ytdR.data : y12R.data;
  // Under a client filter, /reports/range keeps `totals` person-level (VAT/URSSAF
  // never split) and scopes only `clientActivity`. So the revenue figure reads
  // clientActivity when a client is selected, and the person-level totals only
  // for the all-clients view.
  const scoped = clientId ? data?.clientActivity : null;
  const ht = scoped ? scoped.expectedHtCents : (data?.totals.expectedHtCents ?? 0);
  const billable = scoped ? scoped.billableDays : (data?.totals.billableDays ?? 0);

  // "À mettre de côté" — a forward estimate of what to reserve from THIS
  // revenue: URSSAF cotisations + TVA to remit, both a rate on the shown HT so
  // they follow the period and the client filter automatically. This is guidance
  // proportional to the revenue, NOT the person-level ceiling (that stays annual
  // and unsplit on the URSSAF card).
  const ratePct = urssaf?.ratePct ?? 0;
  const vatApplicable = urssaf?.vatApplicable ?? false;
  const urssafAmt = Math.round((ht * ratePct) / 100);
  const tvaAmt = vatApplicable ? Math.round(ht * 0.2) : 0;
  const setTotal = urssafAmt + tvaAmt;

  const filtered = Boolean(clientId);

  const sub =
    period === "month"
      ? monthName(month, { month: "long", year: "numeric" })
      : period === "ytd"
        ? `janv – ${monthName(month, { month: "short" })} ${year}`
        : `${monthName(y12From.slice(0, 7), { month: "short", year: "numeric" })} – ${monthName(month, { month: "short", year: "numeric" })}`;

  const TABS: { key: Period; label: string }[] = [
    { key: "month", label: t("revenue.month") },
    { key: "ytd", label: t("revenue.ytd") },
    { key: "y12", label: t("revenue.y12") },
  ];

  const seg = (active: boolean): React.CSSProperties => ({
    flex: 1,
    minWidth: 0,
    height: 40,
    borderRadius: 8,
    cursor: "pointer",
    font: "600 11.5px Sora",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    transition: ".14s",
    whiteSpace: "nowrap",
    padding: "0 3px",
    border: active ? "1px solid var(--primary)" : "1px solid transparent",
    background: active ? "var(--primary)" : "transparent",
    color: active ? "#fff" : "var(--muted)",
    boxShadow: active ? "0 2px 8px var(--glow)" : "none",
  });

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.05)",
        padding: "16px 17px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ font: "600 11px Sora", letterSpacing: ".07em", textTransform: "uppercase", color: "var(--muted2)" }}>
            {t("revenue.title")}
          </span>
          {filtered && clientName && (
            <span style={{ font: "700 9px Sora", letterSpacing: ".05em", textTransform: "uppercase", background: "color-mix(in srgb, var(--primary) 14%, transparent)", color: "var(--primary-deep)", padding: "3px 7px", borderRadius: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {clientName}
            </span>
          )}
        </div>
        <span style={{ font: "700 10px Sora", letterSpacing: ".05em", border: "1px solid color-mix(in srgb, var(--text) 32%, var(--border))", color: "var(--text)", padding: "3px 8px", borderRadius: 6, flex: "none" }}>
          HT
        </span>
      </div>

      <div role="tablist" aria-label={t("revenue.periodAria")} style={{ display: "flex", gap: 5, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 11, padding: 4, margin: "11px 0 14px" }}>
        {TABS.map((tab) => {
          const active = period === tab.key;
          return (
            <button key={tab.key} role="tab" aria-selected={active} onClick={() => setPeriod(tab.key)} style={seg(active)}>
              {active && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}>
                  <path d="M5 12l4 4 10-10" />
                </svg>
              )}
              {tab.label}
            </button>
          );
        })}
      </div>

      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: 34, letterSpacing: "-.01em", color: "var(--text)", lineHeight: 1.05 }}>
        {formatEuros(ht)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
        <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500 }}>
          {billable === 0 ? t("revenue.noDay") : t("revenue.billableDays", { days: fmtDays(billable) })}
        </span>
        <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--muted2)" }} />
        <span style={{ fontSize: 12.5, color: "var(--muted2)" }}>{sub}</span>
      </div>

      {/* À mettre de côté — URSSAF + TVA, follows the period and client filter. */}
      <div style={{ marginTop: 13, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "11px 13px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
          <span style={{ font: "600 10px Sora", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted2)" }}>
            {t("revenue.setAsideTitle")}
          </span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: 18, color: "var(--text)" }}>
            {formatEuros(setTotal)}
          </span>
        </div>
        <div style={{ display: "flex", gap: 7, marginTop: 9 }}>
          <div style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 9px" }}>
            <div style={{ fontSize: 10, color: "var(--muted2)", fontWeight: 600 }}>{t("revenue.urssafPart", { pct: `~${fmtPct(ratePct)} %` })}</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: 13, color: "var(--text)", marginTop: 2 }}>
              {formatEuros(urssafAmt)}
            </div>
          </div>
          <div style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 9px" }}>
            <div style={{ fontSize: 10, color: "var(--muted2)", fontWeight: 600 }}>{t("revenue.tvaPart", { pct: vatApplicable ? "20 %" : "0 %" })}</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: 13, color: "var(--text)", marginTop: 2 }}>
              {formatEuros(tvaAmt)}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10.5, color: "var(--muted2)", marginTop: 7, lineHeight: 1.4 }}>{t("revenue.setNote")}</div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 7, marginTop: 11 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted2)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", marginTop: 1 }}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8h.01M11 12h1v4h1" />
        </svg>
        <span style={{ fontSize: 11.5, color: "var(--muted2)", lineHeight: 1.45 }}>{t("revenue.clientNote")}</span>
      </div>
    </div>
  );
}
